import { useEffect, useState, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import defaultApi from "@/lib/api";
import { CaretLeft, CaretRight, User, Calendar as CalIcon } from "@phosphor-icons/react";

/**
 * NewStartsCalendar — month-grid view of every ticket that came from an
 * Onboarding form (or has a "start"-labelled key_date) grouped by their
 * key_date. Reuses `Ticket.key_date` — the same value the ticket detail
 * page's Key Date widget renders — so no new schema.
 *
 * Data source: pluggable via props so this same component drives both
 * the agent view (`/api/new-starts`) and the requester portal view
 * (`/api/portal/new-starts`).
 *
 * Props:
 *   - apiClient (optional): axios instance to use. Defaults to the
 *     staff-auth api client.
 *   - fetchUrl (optional): endpoint to hit. Defaults to `/new-starts`.
 *   - ticketLinkPrefix (optional): path prefix for pill/upcoming links.
 *     Defaults to `/tickets`. Set to `/portal/tickets` for the portal.
 *   - title / subtitle (optional): heading overrides.
 */

function startOfMonth(d) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}
function endOfMonth(d) {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0);
}
function ymd(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function parseKeyDate(iso) {
  // key_date is YYYY-MM-DD — construct as local date to avoid TZ drift.
  const [y, m, d] = (iso || "").split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

/**
 * Traffic-light classification per pill:
 *   - "green"  = checklist fully done OR ticket resolved/closed
 *   - "amber"  = some checklist items done (in-progress)
 *   - "red"    = has a checklist but nothing done yet
 *   - "gray"   = no checklist on this ticket (nothing to progress)
 */
function progressColor(t) {
  const total = t.checklist_total || 0;
  const done = t.checklist_done || 0;
  const resolved = t.status === "resolved" || t.status === "closed";
  if (total === 0) return resolved ? "green" : "gray";
  if (done >= total || resolved) return "green";
  if (done === 0) return "red";
  return "amber";
}

const PILL_CLASSES = {
  green: "bg-emerald-100 text-emerald-800 hover:bg-emerald-200",
  amber: "bg-amber-100 text-amber-900 hover:bg-amber-200",
  red: "bg-red-100 text-red-800 hover:bg-red-200",
  gray: "bg-slate-100 text-slate-700 hover:bg-slate-200",
};

function displayName(t) {
  // Stage 4 — for tickets that are still awaiting details (or whose
  // stored subject_name is a placeholder), don't fall back to the
  // requester's name — the requester is usually HR, not the new
  // starter. Show a clear "Name TBC · #N" placeholder instead.
  const rawSubjectName = (t.subject_name || "").trim();
  const placeholderTokens = new Set(["tbc","tba","tbd","?","??","???","n/a","na","not applicable","unknown","not sure","pending"]);
  const subjectIsPlaceholder = !rawSubjectName || placeholderTokens.has(rawSubjectName.toLowerCase());
  if (subjectIsPlaceholder) {
    // If the ticket is awaiting details OR the stored value is a placeholder,
    // surface "Name TBC · #N" instead of the HR submitter.
    if (t.awaiting_details || rawSubjectName) return `Name TBC · #${t.number}`;
  } else {
    return rawSubjectName;
  }
  // Non-awaiting ticket with no subject_name — fall back to requester.
  return t.requester_name || t.requester_email || `#${t.number}`;
}

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export default function NewStartsCalendar({
  apiClient,
  fetchUrl = "/new-starts",
  ticketLinkPrefix = "/tickets",
  title = "New Starts",
  subtitle = "Every onboarding form submission plotted against its start date. Click a name to open the ticket.",
  upcomingLabel = "Upcoming starts",
  undatedLabel = "Awaiting a start date",
  undatedHelp = "These onboarding requests were submitted before the start date was known. They'll appear on the calendar as soon as the requester adds a date.",
}) {
  const api = apiClient || defaultApi;
  const location = useLocation();
  // Jun 2026 — remember region filter per user. Shared across the
  // New Starts and Offboards views: picking a region on one preselects
  // it on the other.
  const regionLsKey = (() => {
    try {
      const u = JSON.parse(localStorage.getItem("hd_user") || "null");
      return u?.id ? `hd_calendar_region_${u.id}` : "hd_calendar_region";
    } catch { return "hd_calendar_region"; }
  })();
  // Where the ticket-detail "Back" button should send the user — the
  // exact URL we're on, so region filter / cursor month survive.
  const backState = {
    from: location.pathname + location.search,
    fromLabel: title || "new starts",
  };
  const [starts, setStarts] = useState(null);
  const [cursor, setCursor] = useState(() => startOfMonth(new Date()));
  const [expandedDays, setExpandedDays] = useState(new Set());
  const [regionFilter, setRegionFilter] = useState(() => {
    try { return localStorage.getItem(regionLsKey) || "__all__"; }
    catch { return "__all__"; }
  });

  useEffect(() => {
    try { localStorage.setItem(regionLsKey, regionFilter); } catch { /* noop */ }
  }, [regionFilter, regionLsKey]);

  useEffect(() => {
    api.get(fetchUrl).then((r) => setStarts(r.data || [])).catch(() => setStarts([]));
  }, [api, fetchUrl]);

  const regionOptions = useMemo(() => {
    const seen = new Set();
    (starts || []).forEach((t) => {
      const r = (t.region || "").trim();
      if (r) seen.add(r);
    });
    return Array.from(seen).sort();
  }, [starts]);

  const filteredStarts = useMemo(() => {
    if (regionFilter === "__all__") return starts || [];
    if (regionFilter === "__none__") return (starts || []).filter((t) => !(t.region || "").trim());
    return (starts || []).filter((t) => (t.region || "").trim() === regionFilter);
  }, [starts, regionFilter]);

  const toggleDay = (dayKey) =>
    setExpandedDays((s) => {
      const n = new Set(s);
      if (n.has(dayKey)) n.delete(dayKey); else n.add(dayKey);
      return n;
    });

  // Bucket by YYYY-MM-DD so we can render the month grid quickly.
  // Stage 4 — awaiting_details tickets with no key_date go into a
  // separate "undated" tray below the grid.
  const buckets = useMemo(() => {
    const m = new Map();
    for (const t of (filteredStarts || [])) {
      const key = (t.key_date || "").slice(0, 10);
      if (!key) continue;
      if (!m.has(key)) m.set(key, []);
      m.get(key).push(t);
    }
    return m;
  }, [filteredStarts]);

  const undated = useMemo(
    () => (filteredStarts || []).filter((t) => !t.key_date && t.awaiting_details),
    [filteredStarts]
  );

  // Build grid — starts on Monday, includes leading/trailing days from
  // adjacent months so the grid is a clean 6 rows.
  const grid = useMemo(() => {
    const first = startOfMonth(cursor);
    const last = endOfMonth(cursor);
    // Monday=0 ... Sunday=6
    const lead = (first.getDay() + 6) % 7;
    const days = [];
    for (let i = -lead; i < 42 - lead && days.length < 42; i++) {
      const d = new Date(first);
      d.setDate(first.getDate() + i);
      days.push({
        date: d,
        key: ymd(d),
        inMonth: d.getMonth() === cursor.getMonth(),
        isToday: ymd(d) === ymd(new Date()),
        isPast: d < new Date(new Date().setHours(0, 0, 0, 0)),
      });
      if (d >= last && days.length >= 35 && d.getDay() === 0) break;
    }
    return days;
  }, [cursor]);

  const monthLabel = cursor.toLocaleDateString("en-GB", { month: "long", year: "numeric" });
  const upcoming = useMemo(() => {
    const today = ymd(new Date());
    return (filteredStarts || []).filter((t) => (t.key_date || "") >= today);
  }, [filteredStarts]);

  return (
    <div className="space-y-6" data-testid="new-starts-page">
      <div className="flex items-baseline justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight flex items-center gap-2">
            <CalIcon size={22} weight="duotone" className="text-[#4f4c90]" /> {title}
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {subtitle}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {regionOptions.length > 0 && (
            <select
              value={regionFilter}
              onChange={(e) => setRegionFilter(e.target.value)}
              data-testid="new-starts-region-filter"
              className="h-9 rounded-md border border-slate-200 bg-white px-2 text-xs text-slate-700 hover:bg-slate-50"
              title="Filter by region"
            >
              <option value="__all__">All regions</option>
              {regionOptions.map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
              <option value="__none__">Untagged</option>
            </select>
          )}
          <button
            onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))}
            className="h-9 w-9 rounded-md border border-slate-200 bg-white hover:bg-slate-50 flex items-center justify-center"
            data-testid="new-starts-prev"
          ><CaretLeft size={14} /></button>
          <button
            onClick={() => setCursor(startOfMonth(new Date()))}
            className="h-9 px-3 rounded-md border border-slate-200 bg-white hover:bg-slate-50 text-xs"
            data-testid="new-starts-today"
          >Today</button>
          <button
            onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))}
            className="h-9 w-9 rounded-md border border-slate-200 bg-white hover:bg-slate-50 flex items-center justify-center"
            data-testid="new-starts-next"
          ><CaretRight size={14} /></button>
        </div>
      </div>

      <div className="hairline rounded-lg bg-white overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 flex items-baseline justify-between gap-4 flex-wrap">
          <div className="font-heading font-semibold text-lg">{monthLabel}</div>
          <div className="flex items-center gap-3 text-[11px] text-slate-500 font-mono">
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500" /> nothing done</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500" /> in progress</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500" /> complete</span>
            <span className="flex items-center gap-1"><span className="w-3 h-2 rounded border border-dashed border-amber-500 bg-white" /> awaiting details</span>
          </div>
        </div>

        <div className="grid grid-cols-7 border-b border-slate-100 bg-slate-50/40">
          {WEEKDAYS.map((d) => (
            <div key={d} className="text-[10px] uppercase tracking-wider text-slate-500 font-medium py-2 px-2 text-center">{d}</div>
          ))}
        </div>

        <div className="grid grid-cols-7 auto-rows-auto">
          {starts === null ? (
            <div className="col-span-7 py-10 text-center text-slate-500 italic text-sm">Loading…</div>
          ) : grid.map((cell) => {
            const items = buckets.get(cell.key) || [];
            const isExpanded = expandedDays.has(cell.key);
            const visibleItems = isExpanded ? items : items.slice(0, 3);
            return (
              <div
                key={cell.key}
                className={`border-r border-b border-slate-100 p-1.5 flex flex-col gap-1 min-h-[88px] ${
                  cell.inMonth ? "bg-white" : "bg-slate-50/30"
                } ${cell.isToday ? "ring-2 ring-inset ring-[#4f4c90]/30" : ""}`}
                data-testid={`day-${cell.key}`}
              >
                <div className={`text-[11px] font-mono ${cell.inMonth ? "text-slate-600" : "text-slate-400"} ${cell.isToday ? "font-bold text-[#4f4c90]" : ""}`}>
                  {cell.date.getDate()}
                </div>
                {visibleItems.map((t) => {
                  const color = progressColor(t);
                  const pillCls = cell.isPast && color === "gray"
                    ? "bg-slate-100 text-slate-500"
                    : PILL_CLASSES[color];
                  // Stage 4 — dashed amber outline signals "still awaiting
                  // details from the requester". Distinct from the
                  // traffic-light which reflects the checklist progress.
                  const provisional = t.awaiting_details ? " border border-dashed border-amber-500" : "";
                  return (
                    <Link
                      key={t.id}
                      to={`${ticketLinkPrefix}/${t.id}`}
                      state={backState}
                      className={`rounded px-1.5 py-1 text-[11px] leading-tight truncate flex items-center gap-1 ${pillCls}${provisional}`}
                      data-testid={`new-start-pill-${t.id}`}
                      title={`#${t.number} ${displayName(t)} — ${t.checklist_done}/${t.checklist_total} done${t.awaiting_details ? " · awaiting details" : ""}`}
                    >
                      <User size={10} weight="duotone" className="shrink-0" />
                      <span className="truncate">{displayName(t)}</span>
                    </Link>
                  );
                })}
                {items.length > 3 && (
                  <button
                    type="button"
                    onClick={() => toggleDay(cell.key)}
                    data-testid={`day-toggle-${cell.key}`}
                    className="text-[10px] text-[#4f4c90] font-mono hover:underline text-left px-1.5 py-0.5 -mx-1.5 rounded hover:bg-[#4f4c90]/5"
                  >
                    {isExpanded ? "− show less" : `+ ${items.length - 3} more`}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {undated.length > 0 && (
        <div className="hairline rounded-lg bg-amber-50/50 border-amber-200 p-5" data-testid="new-starts-undated-tray">
          <div className="text-[11px] uppercase tracking-wider text-amber-800 font-medium mb-3">
            {undatedLabel} — {undated.length}
          </div>
          <div className="text-[11px] text-amber-800/80 mb-3">
            {undatedHelp}
          </div>
          <div className="flex flex-wrap gap-2">
            {undated.map((t) => (
              <Link
                key={t.id}
                to={`${ticketLinkPrefix}/${t.id}`}
                state={backState}
                className="chip !bg-white !text-amber-900 border border-dashed border-amber-400 hover:!bg-amber-50 flex items-center gap-1"
                data-testid={`new-starts-undated-${t.id}`}
              >
                <User size={11} weight="duotone" /> {displayName(t)}
                <span className="text-[10px] font-mono text-amber-700">·#{t.number}</span>
              </Link>
            ))}
          </div>
        </div>
      )}

      {upcoming.length > 0 && (
        <div className="hairline rounded-lg bg-white p-5" data-testid="new-starts-upcoming">
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-medium mb-3">{upcomingLabel}</div>
          <div className="divide-y divide-slate-100 -mx-5">
            {upcoming.map((t) => {
              const d = parseKeyDate(t.key_date);
              const nice = d ? d.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" }) : t.key_date;
              return (
                <Link
                  key={t.id}
                  to={`${ticketLinkPrefix}/${t.id}`}
                  state={backState}
                  className="flex items-center gap-3 px-5 py-2 hover:bg-slate-50"
                  data-testid={`new-starts-upcoming-${t.id}`}
                >
                  <div className="w-9 h-9 rounded-md bg-[#4f4c90]/10 border border-[#4f4c90]/20 flex flex-col items-center justify-center shrink-0">
                    <div className="text-[9px] uppercase text-[#4f4c90] font-mono leading-none">{d?.toLocaleDateString("en-GB", { month: "short" })}</div>
                    <div className="text-sm font-bold text-[#4f4c90] leading-none mt-0.5">{d?.getDate()}</div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{displayName(t)}</div>
                    <div className="text-[11px] text-slate-500 font-mono truncate">
                      {nice} · #{t.number}
                      {t.checklist_total > 0 && ` · ${t.checklist_done}/${t.checklist_total} done`}
                    </div>
                  </div>
                  {(() => {
                    const c = progressColor(t);
                    return c !== "gray" ? (
                      <span className={`w-2 h-2 rounded-full ${c === "green" ? "bg-emerald-500" : c === "amber" ? "bg-amber-500" : "bg-red-500"}`} />
                    ) : null;
                  })()}
                  {t.region && <span className="chip !text-[10px] !py-0.5">{t.region}</span>}
                </Link>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
